'use client';

import Link from 'next/link';
import Image from 'next/image';

interface FooterProps {
  locale?: 'en' | 'si' | 'ta';
}

const Footer = ({ locale = 'en' }: FooterProps) => {
  return (
    <footer
      className="font-sans relative overflow-hidden print:hidden"
      style={{ background: 'linear-gradient(to bottom, #65A3E5, #4A84C2)', fontWeight: 300 }}
    >
      <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-6 pt-16 pb-8">
        
        {/* Main Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-12 mb-12">
          
          {/* Logo & About Column */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <Link href="/" aria-label="Baasil Topup Home">
              <Image
                src="/assets/logo.svg"
                alt="Baasil Topup"
                width={160}
                height={96}
                className="w-40 h-auto mb-6"
              />
            </Link>
            <p className="text-xs text-[#BED6EF] leading-relaxed mb-6">
              Sri Lanka's #1 trusted destination for instant Free Fire diamond top-ups. Experience seamless gaming with automated delivery.
            </p>
          </div>

          {/* Explore Column */}
          <nav aria-label="Footer Navigation - Explore" className="col-span-1">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3 text-white">
              {locale === 'ta' ? 'ஆராயுங்கள்' : 'Explore'}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link href="/about" className="text-xs font-medium text-[#BED6EF] hover:text-white transition-colors duration-200">
                  {locale === 'ta' ? 'எங்களை பற்றி' : 'About Us'}
                </Link>
              </li>
              <li>
                <Link href={locale === 'si' ? '/si/how-to-topup' : '/how-to-topup'} className="text-xs font-medium text-[#BED6EF] hover:text-white transition-colors duration-200">
                  {locale === 'ta' ? 'எப்படி Topup செய்வது' : 'How to Topup'}
                </Link>
              </li>
              <li>
                <a href="#subscriptions" className="text-xs font-medium text-[#BED6EF] hover:text-white transition-colors duration-200" onClick={(e) => { e.preventDefault(); document.getElementById('subscriptions')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>
                  {locale === 'ta' ? 'சந்தாக்கள் மற்றும் தொகுப்புகள்' : 'Subscriptions & Bundles'}
                </a>
              </li>
              <li>
                <a href="#diamonds" className="text-xs font-medium text-[#BED6EF] hover:text-white transition-colors duration-200" onClick={(e) => { e.preventDefault(); document.getElementById('diamonds')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>
                  {locale === 'ta' ? 'டயமண்ட்ஸ்' : 'Diamonds'}
                </a>
              </li>
            </ul>
          </nav>

          {/* Social Column */}
          <nav aria-label="Footer Navigation - Social Media" className="col-span-1">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3 text-white">
              {locale === 'ta' ? 'பின்தொடருங்கள்' : 'Follow'}
            </h3>
            <ul className="space-y-2">
              <li>
                <a href="https://www.youtube.com/@baasilff" target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-[#BED6EF] hover:text-red-500 transition-colors duration-200">
                  Youtube
                </a>
              </li>
              <li>
                <a href="https://www.facebook.com/FreeFireBaasil/" target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-[#BED6EF] hover:text-blue-500 transition-colors duration-200">
                  Facebook
                </a>
              </li>
              <li>
                <a href="https://www.tiktok.com/@baasilisback" target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-[#BED6EF] hover:text-pink-500 transition-colors duration-200">
                  TikTok
                </a>
              </li>
              <li>
                <a href="https://www.instagram.com/baasil_07/" target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-[#BED6EF] hover:text-purple-500 transition-colors duration-200">
                  Instagram
                </a>
              </li>
            </ul>
          </nav>

          {/* Contact Column */}
          <nav aria-label="Footer Navigation - Contact" className="col-span-1">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3 text-white">Contact Us</h3>
            <ul className="space-y-2">
              <li>
                <a href="https://wa.me/94742300407" target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-[#BED6EF] hover:text-green-500 transition-colors duration-200">
                  +94 74 230 0407
                </a>
              </li>
              <li>
                <a href="mailto:baasil@baasiltopup.com" className="text-xs font-medium text-[#BED6EF] hover:text-white transition-colors duration-200">
                  baasil@baasiltopup.com
                </a>
              </li>
            </ul>
          </nav>

          {/* Legal Column */}
          <nav aria-label="Footer Navigation - Legal" className="col-span-1">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3 text-white">Legal</h3>
            <ul className="space-y-2">
              <li>
                <Link href="/terms" className="text-xs font-medium text-[#BED6EF] hover:text-white transition-colors duration-200">
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link href="/privacy-policy" className="text-xs font-medium text-[#BED6EF] hover:text-white transition-colors duration-200">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link href="/brdoc" className="text-xs font-medium text-[#BED6EF] hover:text-white transition-colors duration-200">
                  Business Registration
                </Link>
              </li>
            </ul>
          </nav>
        </div>

        <hr className="border-white/15 my-8" />

        {/* Bottom Section: Payment Methods & Reviews */}
        <div className="flex flex-col xl:flex-row justify-between items-center gap-10 mb-10">
          
          {/* Payment Methods */}
          <div className="w-full xl:w-auto">
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#BED6EF] mb-4 text-center xl:text-left">
              Secure Payments
            </p>
            <div className="grid grid-cols-4 gap-2.5 max-w-lg mx-auto xl:mx-0">
              {/* Visa */}
              <div aria-label="Visa Payment" title="Visa" className="flex items-center justify-center px-3 py-2 rounded-lg bg-black/10 border border-white/10 backdrop-blur-sm shadow-sm transition-transform hover:-translate-y-0.5">
                <img src="/assets/visa.png" alt="Visa" width={48} height={16} className="h-4 w-auto object-contain" />
                <span className="sr-only">Visa</span>
              </div>
              
              {/* MasterCard */}
              <div aria-label="MasterCard Payment" title="MasterCard" className="flex items-center justify-center px-3 py-2 rounded-lg bg-black/10 border border-white/10 backdrop-blur-sm shadow-sm transition-transform hover:-translate-y-0.5">
                <svg viewBox="0 0 36 22" className="h-4 w-auto">
                  <circle cx="11.5" cy="11.5" r="11.5" fill="#EB001B"/>
                  <circle cx="24.5" cy="11.5" r="11.5" fill="#F79E1B"/>
                  <path fill="#FF5F00" d="M18 20.375A11.462 11.462 0 0113.625 11.5 11.462 11.462 0 018 2.625 11.462 11.462 0 0122.375 11.5 11.462 11.462 0 018 20.375z"/>
                </svg>
                <span className="sr-only">MasterCard</span>
              </div>
              
              {/* Amex */}
              <div aria-label="American Express Payment" title="American Express" className="flex items-center justify-center px-3 py-2 rounded-lg bg-black/10 border border-white/10 backdrop-blur-sm shadow-sm transition-transform hover:-translate-y-0.5">
                <div className="bg-sky-500 px-1.5 py-0.5 rounded flex items-center justify-center font-black text-white text-[9px] tracking-tighter h-4 leading-none">AMEX</div>
                <span className="sr-only">American Express</span>
              </div>
              
              {/* Lanka QR */}
              <div aria-label="Lanka QR Payment" title="Lanka QR" className="flex items-center justify-center px-3 py-2 rounded-lg bg-black/10 border border-white/10 backdrop-blur-sm shadow-sm transition-transform hover:-translate-y-0.5">
                <img src="/assets/lankaqr.jpg" alt="Lanka QR" width={64} height={16} className="h-4 w-auto object-contain rounded-[2px] mix-blend-multiply" />
                <span className="sr-only">Lanka QR</span>
              </div>
              
              {/* eZ Cash */}
              <div aria-label="eZ Cash Payment" title="eZ Cash" className="flex items-center justify-center px-3 py-2 rounded-lg bg-black/10 border border-white/10 backdrop-blur-sm shadow-sm transition-transform hover:-translate-y-0.5">
                <img src="/assets/ez-cash-logo.png" alt="eZ Cash" width={48} height={16} className="h-4 w-auto object-contain" />
                <span className="sr-only">eZ Cash</span>
              </div>
              
              {/* Binance */}
              <div aria-label="Binance Pay" title="Binance Pay" className="flex items-center justify-center px-3 py-2 rounded-lg bg-black/10 border border-white/10 backdrop-blur-sm shadow-sm transition-transform hover:-translate-y-0.5">
                <img src="/assets/BinancePay.svg" alt="Binance" width={80} height={16} className="h-4 w-auto object-contain" />
                <span className="sr-only">Binance</span>
              </div>
              
              {/* KuCoin */}
              <div aria-label="KuCoin Pay" title="KuCoin Pay" className="flex items-center justify-center px-3 py-2 rounded-lg bg-black/10 border border-white/10 backdrop-blur-sm shadow-sm transition-transform hover:-translate-y-0.5">
                <img src="/assets/Kucoin.svg" alt="KuCoin" width={80} height={16} className="h-4 w-auto object-contain" />
                <span className="sr-only">KuCoin</span>
              </div>
              
              {/* Bybit Pay */}
              <div aria-label="Bybit Pay" title="Bybit Pay" className="flex items-center justify-center px-3 py-2 rounded-lg bg-black/10 border border-white/10 backdrop-blur-sm shadow-sm transition-transform hover:-translate-y-0.5">
                <svg viewBox="0 0 563 121" className="h-3.5 w-auto" fill="none">
                  <path d="M242.47 96.2938V0.299805H261.212V96.2938H242.47Z" fill="#FF9C2E"/>
                  <path d="M0 120.295V24.3008H38.559C57.3006 24.3008 68.2228 34.8146 68.2228 51.2652C68.2228 61.9185 61.2103 68.7915 56.3593 71.0826C62.1566 73.777 69.5725 79.8482 69.5725 92.6531C69.5725 110.583 57.3055 120.295 40.1826 120.295H0ZM37.0748 41.0153H18.7366V63.1287H37.0748C45.0286 63.1287 49.4812 58.6811 49.4812 52.072C49.4812 45.4629 45.0336 41.0153 37.0748 41.0153ZM38.29 79.9827H18.7416V103.575H38.29C46.7818 103.575 50.8309 98.1814 50.8309 91.7118C50.8309 85.2421 46.7868 79.9827 38.29 79.9827Z" fill="white"/>
                  <path d="M119.756 80.924V120.295H101.149V80.924L72.2968 24.2958H92.6571L110.587 62.9893L128.248 24.2958H148.608L119.756 80.924Z" fill="white"/>
                  <path d="M158.449 120.295V24.3008H197.008C215.75 24.3008 226.672 34.8146 226.672 51.2652C226.672 61.9185 219.66 68.7915 214.809 71.0826C220.606 73.777 228.022 79.8482 228.022 92.6531C228.022 110.583 215.755 120.295 198.632 120.295H158.449ZM195.524 41.0153H177.186V63.1287H195.524C203.478 63.1287 207.931 58.6811 207.931 52.072C207.931 45.4629 203.483 41.0153 195.524 41.0153ZM196.739 79.9827H177.191V103.575H196.739C205.231 103.575 209.28 98.1814 209.28 91.7118C209.28 85.2421 205.236 79.9827 196.739 79.9827Z" fill="white"/>
                  <path d="M321.506 41.0203V120.3H302.764V41.0203H277.687V24.3008H346.583V41.0203H321.506Z" fill="white"/>
                  <path d="M445.613 55.7343C445.613 69.6198 435.85 78.5151 421.747 78.5151H401.244V110.3H393V33.062H421.747C435.85 33.062 445.613 41.8489 445.613 55.7343ZM437.368 55.7343C437.368 45.5372 430.643 40.4386 420.988 40.4386H401.244V71.1385H420.988C430.643 71.1385 437.368 65.9314 437.368 55.7343Z" fill="white"/>
                  <path d="M509.733 110.3H500.946L494.763 92.8345H461.025L454.842 110.3H446.055L474.477 33.062H481.42L509.733 110.3ZM492.376 85.6748L478.057 44.8863L463.52 85.6748H492.376Z" fill="white"/>
                  <path d="M562.147 33.062L539.367 78.4067V110.3H531.122V78.4067L508.124 33.062H517.02L535.244 70.0537L553.252 33.062H562.147Z" fill="white"/>
                </svg>
                <span className="sr-only">Bybit Pay</span>
              </div>
            </div>
          </div>

          {/* Reviews */}
          <div className="w-full xl:w-auto">
             <p className="text-[10px] font-bold uppercase tracking-widest text-[#BED6EF] mb-4 text-center xl:text-left">
              Verified Reviews
            </p>
            <div className="flex flex-wrap gap-4 justify-center xl:justify-end">
              <a href="https://maps.app.goo.gl/gzqRBWSS2oUUTKLJ7" target="_blank" rel="noopener noreferrer" aria-label="Google Reviews" className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-white hover:bg-gray-50 transition border border-gray-200 shadow-sm group">
                <svg viewBox="0 0 48 48" className="w-5 h-5 flex-shrink-0">
                  <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                  <path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                  <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                  <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
                </svg>
                <span className="text-xs uppercase tracking-wider text-slate-700 font-bold transition-colors">Google</span>
              </a>
              <a href="https://www.trustpilot.com/evaluate/baasiltopup.com" target="_blank" rel="noopener noreferrer" aria-label="Trustpilot Reviews" className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-white hover:bg-gray-50 transition border border-gray-200 shadow-sm group">
                <svg viewBox="0 0 48 48" className="w-5 h-5 flex-shrink-0">
                  <path fill="#00B67A" d="M24 2.3 29.4 17l16.1.2-12.7 9.9 4.7 16.3L24 34.5 10.5 43.4l4.7-16.3-12.7-9.9L18.6 17z"/>
                  <path fill="#DCDCDC" d="M24 28.6 14.3 35l1.8-6.3z"/>
                  <path fill="#00B67A" d="M24 2.3 29.4 17l16.1.2-12.7 9.9 4.7 16.3"/>
                </svg>
                <span className="text-xs uppercase tracking-wider text-slate-700 font-bold transition-colors">Trustpilot</span>
              </a>
            </div>
          </div>
        </div>
        
        {/* Trademark Disclaimer */}
        <div className="pt-6 pb-2 border-t border-white/15 text-center">
          <p className="text-[11px] leading-relaxed text-[#BED6EF] max-w-4xl mx-auto font-sans">
            <strong className="text-white font-semibold">Disclaimer:</strong> Baasil Topup is an independent third-party digital service provider and is not affiliated with, endorsed by, or sponsored by any game developers or publishers. All product names, logos, and brands are property of their respective owners.
          </p>
        </div>

        {/* Copyright */}
        <div className="flex flex-col md:flex-row justify-between items-center pt-6 border-t border-white/15">
          <p className="text-[10px] uppercase tracking-widest text-[#BED6EF] text-center md:text-left mb-3 md:mb-0">
            &copy; {new Date().getFullYear()} Baasil Topup. All rights reserved.
          </p>
          <p className="text-[10px] uppercase tracking-widest text-[#BED6EF] text-center md:text-right">
            Developed by{' '}
            <a href="https://ashanhimantha.com" className="text-white hover:text-blue-200 font-bold transition-colors" target="_blank" rel="noopener noreferrer" aria-label="Developer Portfolio">
              Ashan Himantha
            </a>
          </p>
        </div>
      </div>

      <div className="relative z-[5] max-w-6xl mx-auto px-4 md:px-6 -mt-2 md:-mt-4">
        <Image
          src="/assets/footer-characters.webp"
          alt="BAASIL FF Characters"
          width={1152}
          height={400}
          className="w-full h-auto object-contain -mb-6 md:-mb-24"
        />
      </div>
    </footer>
  );
};

export default Footer;
